import React, { createContext, useState, useEffect } from "react";
import { doc, setDoc, getDoc } from "firebase/firestore";
import {
  signInWithPopup,
  signInWithRedirect,
  OAuthProvider,
} from "firebase/auth";
import { auth, db, googleProvider, appleProvider } from "../config/firebase";

import { ShowToast, Severty } from "../helper/toast";
import ROUTES from "../Routes";
import { authAPI } from "../services/api";
import { useNavigate } from "react-router";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [session, setSession] = useState({ token: null });
  const [userProfile, setUserProfile] = useState();
  const navigate = useNavigate();
  const [headerFilterDate, setHeaderFilterDate] = useState({
    start: null,
    end: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem("token");
        const userProfileData = localStorage.getItem("userProfile");

        if (token && userProfileData) {
          const user = JSON.parse(userProfileData);
          setUserProfile(user);
          setIsLoggedIn(true);
          setSession({ token: token });
        } else {
          // Clear any invalid data
          localStorage.removeItem("token");
          localStorage.removeItem("userProfile");
          setIsLoggedIn(false);
          setSession({ token: null });
          setUserProfile(null);
        }
      } catch (error) {
        // Clear invalid data on error
        localStorage.removeItem("token");
        localStorage.removeItem("userProfile");
        setIsLoggedIn(false);
        setSession({ token: null });
        setUserProfile(null);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    try {
      const response = await authAPI.signin(credentials);

      // Check if login was successful based on your API response structure
      if (response.status === 200 && response.data && response.data.token) {
        const token = response.data.token;
        let userData = response.data.user;
        userData.balance = response.data.balance;

        // Store token and user data
        localStorage.setItem("token", token);
        localStorage.setItem("userProfile", JSON.stringify(userData));
        window.dispatchEvent(new Event("storage"));

        // Update state
        setSession({ token });
        setUserProfile(userData);
        setIsLoggedIn(true);

        // ✅ Firestore Sync (Update or Create User)
        const userRef = doc(db, "users", userData._id);
        const userSnap = await getDoc(userRef);
        const userPayload = {
          _id: userData._id,
          balance: userData.balance || 0,
          countryCode: userData.countryCode,
          name: userData.name.toLowerCase() || "",
          email: userData.email || "",
          phone: userData.phone || "",
          customerUniqueToken: userData.customerUniqueToken,
          deviceToken: userData.deviceToken || "",
          handleName: userData.userId,
          isOnline: false,
          lastLogin: userData.lastLogin || new Date().toISOString(),
          lastActive: userData.lastActive || new Date().toISOString(),
          profileImage: userData.profilePic,
          planType: userData.subscriptionPlan || "free",
          createdAt: new Date().toISOString(),
        };

        if (userSnap.exists()) {
          // ✅ Update existing Firestore document
          await setDoc(userRef, userPayload, { merge: true });
        } else {
          // 🆕 Create new Firestore document
          await setDoc(userRef, {
            ...userPayload,
            createdAt: new Date().toISOString(),
          });
        }

        return { success: true, data: response };
      } else {
        const errorMessage = response.message || "Login failed";
        ShowToast(errorMessage, Severty.ERROR);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Login error:", error);
      // Handle different types of errors
      let errorMessage = "Login failed. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }

      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    setLoading(true);
    try {
      const response = await authAPI.signup(userData);
      // Check if signup was successful based on your API response structure
      if (response.status === 201 && response.data && response.data.token) {
        const token = response.data.token;
        const user = response.data.user;

        // Store token and user data
        localStorage.setItem("token", token);
        localStorage.setItem("userProfile", JSON.stringify(user));
        window.dispatchEvent(new Event("storage"));

        // Update state
        setSession({ token });
        setUserProfile(user);
        setIsLoggedIn(true);

        // ✅ Save user to Firestore using their _id
        const userRef = doc(db, "users", user._id);
        await setDoc(userRef, {
          _id: user._id,
          balance: user.balance || 0,
          countryCode: user.countryCode,
          name: user.name.toLowerCase() || "",
          email: user.email || "",
          phone: user.phone || "",
          customerUniqueToken: user.customerUniqueToken,
          deviceToken: user.deviceToken || "",
          handleName: user.userId,
          isOnline: false,
          lastLogin: user.lastLogin || new Date().toISOString(),
          lastActive: user.lastActive || new Date().toISOString(),
          profileImage: user.profilePic,
          planType: user.subscriptionPlan || "free",
          createdAt: new Date().toISOString(),
        });

        // Send OTP

        // ShowToast(response.message || "Signup successful!", Severty.SUCCESS);
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Signup failed", Severty.ERROR);
        return { success: false, error: response.message || "Signup failed" };
      }
    } catch (error) {
      const errorMessage =
        error.message || error.error || "Signup failed. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const firebaseAuth = async (userData) => {
    try {
      const response = await authAPI.firebaseAuth(userData);
      if (response.status === 200 && response.data && response.data.token) {
        const token = response.data.token;
        let userData = response.data.user;
        userData.balance = response.data.balance;

        // Store token and user data
        localStorage.setItem("token", token);
        localStorage.setItem("userProfile", JSON.stringify(userData));

        // Update state
        setSession({ token });
        setUserProfile(userData);
        setIsLoggedIn(true);

        return { success: true, data: response };
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  // const logout = async () => {
  //   setLoading(true);
  //   try {
  //     // Call the logout API
  //     // await authAPI.logout();
  //   } catch (error) {
  //     console.error("Logout API error:", error);
  //   } finally {
  //     // Always clear local data regardless of API response
  //     localStorage.removeItem("token");
  //     localStorage.removeItem("userProfile");
  //     localStorage.removeItem("plan_type");

  //     // Update state
  //     setIsLoggedIn(false);
  //     setSession({ token: null });
  //     setUserProfile(null);

  //     ShowToast("Logged out successfully!", Severty.SUCCESS);

  //     // Use replace to prevent back button issues
  //     window.location.replace(ROUTES.LOGIN);
  //   }
  // };

  const logout = async () => {
    try {
      // Always clear local data regardless of API response
      localStorage.removeItem("token");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("plan_type");
      localStorage.removeItem("walletSummary");
      localStorage.removeItem("checkoutData");

      // Update state
      setIsLoggedIn(false);
      setSession({ token: null });
      setUserProfile(null);
      // Use replace to prevent back button issues
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error("Logout API error:", error);
    }
  };

  // SignIn with google
  const googleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Google Login Error:", error);
      return { success: false, message: error.message };
    }
  };

  // SignIn with apple
  const appleSignIn = async () => {
    try {
      appleProvider.addScope("email");
      appleProvider.addScope("name");
      try {
        const result = await signInWithPopup(auth, appleProvider);
        return { success: true, user: result.user, credential: result };
      } catch (popupError) {
        await signInWithRedirect(auth, appleProvider);
        return { success: null, message: "redirecting" };
      }
    } catch (error) {
      console.error("Apple Sign-in Error:", error);
      return {
        success: false,
        message: error.message || "Apple sign-in failed",
      };
    }
  };

  // Generate Otp
  const generateOtp = async (data) => {
    try {
      const response = await authAPI.generateOtp(data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const forgetPassword = async (credentials) => {
    setLoading(true);
    try {
      const response = await authAPI.forgetPassword(credentials);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // verify otp
  const verifyOtp = async (otpData) => {
    try {
      const response = await authAPI.verifyOtp(otpData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // reset password
  const resetPassword = async (passwordData) => {
    try {
      const response = await authAPI.resetPassword(passwordData);
      return response;
    } catch (error) {
      console.log(error, "error", error);
      throw error;
    }
  };

  // get profile
  const getProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      return response;
    } catch (error) {
      console.log("Error occurred on get profile : ", error);
    }
  };

  // get profile
  const getProfileById = async (id) => {
    try {
      const response = await authAPI.getProfileById(id);
      return response;
    } catch (error) {
      console.log("Error occurred on get profile : ", error);
    }
  };

  // get connections
  const getConnections = async (query) => {
    try {
      const response = await authAPI.getConnections(query);
      return response;
    } catch (error) {
      console.log("Error occurred on get connections : ", error);
    }
  };

  // update profile
  const updateProfile = async (formData) => {
    try {
      const response = await authAPI.updateProfile(formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      console.log("Error occurred on update profile : ", error);
    }
  };

  // get posts
  const getPosts = async (userId) => {
    try {
      const response = await authAPI.getPosts(userId);
      return response;
    } catch (error) {
      console.log("Error occurred on get posts : ", error);
    }
  };

  // create-post
  const createPost = async (post) => {
    try {
      const response = await authAPI.createPost(post);
      return response;
    } catch (error) {
      console.log("Error occurred on create post: ", error);
    }
  };

  // create comment on post
  const createComment = async (postId, content) => {
    try {
      const response = await authAPI.createComment(postId, content);
      return response;
    } catch (error) {
      console.log("Error occurred on create comment : ", error);
    }
  };

  // like post
  const likePost = async (postId) => {
    try {
      const response = await authAPI.likePost(postId);
      return response;
    } catch (error) {
      console.log("Error occurred on like post : ", error);
    }
  };

  const deletePost = async (postId) => {
    try {
      const response = await authAPI.deletePost(postId);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // follow user
  const followUser = async (userId) => {
    try {
      const response = await authAPI.followUser(userId);
      return response;
    } catch (error) {
      console.log("Error occurred on follow user : ", error);
    }
  };

  // un-follow user
  const unfollowUser = async (userId) => {
    try {
      const response = await authAPI.unfollowUser(userId);
      return response;
    } catch (error) {
      console.log("Error occurred on unfollow user : ", error);
    }
  };

  // Change or update password
  const changePassword = async (data) => {
    try {
      const response = await authAPI.changePassword(data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Sent Otp on number
  const sentSMS = async (data) => {
    try {
      const response = await authAPI.sentSMS(data);
      return response;
    } catch (error) {
      return error;
    }
  };

  // Get Notifications
  const getNotification = async (params) => {
    try {
      const response = await authAPI.getNotification(params);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Get Trophies
  const getTrophies = async (userId) => {
    try {
      const response = await authAPI.getTrophies(userId);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Get Activities Log
  const getActivityLog = async (data) => {
    try {
      const response = await authAPI.getActivityLog(data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        session,
        setSession,
        userProfile,
        setUserProfile,
        headerFilterDate,
        setHeaderFilterDate,
        loading,
        setLoading,
        login,
        signup,
        logout,
        forgetPassword,
        resetPassword,
        verifyOtp,
        getProfile,
        getConnections,
        getProfileById,
        updateProfile,
        followUser,
        unfollowUser,
        createPost,
        likePost,
        deletePost,
        createComment,
        getPosts,
        changePassword,
        generateOtp,
        sentSMS,
        googleSignIn,
        appleSignIn,
        firebaseAuth,
        getNotification,
        getTrophies,
        getActivityLog,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
